/**
 * How long after the page wakes a failed request is still blamed on the
 * network. A phone that thaws a tab runs its queued timers and refetches
 * before its radio is back: in Better Stack (2026-10-09) the drops cluster on
 * the first requests after a return to the app, together with failures of
 * unrelated hosts (api2.trendaro.cz, Google Analytics), while RTDB was up.
 */
export const WAKE_GRACE_MS = 10_000;

type PageWakeEnv = {
  win: EventTarget;
  doc: EventTarget & {visibilityState: DocumentVisibilityState};
  nav: {onLine: boolean};
  clock: () => number;
};

export type UnreliableReason = "hidden" | "offline" | "wake";

export type PageWakeTracker = {
  /**
   * Why a failed request now says nothing about our servers, or undefined
   * when it does. The reason is logged so WAKE_GRACE_MS can be re-measured.
   */
  unreliableReason: () => UnreliableReason | undefined;
};

export const createPageWakeTracker = ({
  win,
  doc,
  nav,
  clock,
}: PageWakeEnv): PageWakeTracker => {
  let lastWakeAt = Number.NEGATIVE_INFINITY;

  const markWake = () => {
    lastWakeAt = clock();
  };
  const markWakeIfVisible = () => {
    if (doc.visibilityState === "visible") markWake();
  };

  win.addEventListener("online", markWake);
  // Only a back/forward-cache restore: a first load is no wake-up, and muting
  // its failures would hide an outage that hits every fresh visit.
  win.addEventListener("pageshow", (event) => {
    if ((event as PageTransitionEvent).persisted) markWake();
  });
  doc.addEventListener("visibilitychange", markWakeIfVisible);
  // Page Lifecycle `resume`: Chrome thawing a frozen tab, which may stay
  // visible the whole time and so fires no visibilitychange.
  doc.addEventListener("resume", markWakeIfVisible);

  return {
    unreliableReason: () => {
      if (doc.visibilityState === "hidden") return "hidden";
      if (!nav.onLine) return "offline";
      // A negative span is a clock that stepped back, not a recent wake: it
      // must not keep muting drops until real time catches up.
      const sinceWake = clock() - lastWakeAt;
      if (sinceWake >= 0 && sinceWake < WAKE_GRACE_MS) return "wake";
      return undefined;
    },
  };
};

export const pageWake = createPageWakeTracker({
  win: window,
  doc: document,
  nav: navigator,
  // Monotonic: phones often correct their wall clock right after waking.
  clock: () => performance.now(),
});
